/**
 * Momcozy Baby Name API 请求客户端。
 *
 * Apifox 项目: 6087876
 * 项目 ID 仅用于标记接口文档来源，不会随请求发送给 API。
 * 除通用 HTTP 方法外，本模块还封装了 Apifox 项目中定义的全部
 * Baby Name 业务接口，可直接调用。
 *
 * @example
 * var result = await MomcozyBabyNameRequest.generateNames({
 *   filter: { gender: 'F', surname: 'Smith' },
 *   page: 1,
 *   pagesize: 10,
 * })
 * var detail = await MomcozyBabyNameRequest.getNameInfo('Olivia')
 * await MomcozyBabyNameRequest.addMyBabyName({
 *   baby_name: { first_name: 'Olivia', gender: 'girl' },
 *   last_name: 'Smith',
 * })
 */
(function (root, factory) {
  var api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.MomcozyBabyNameRequest = api;
  }
})(typeof window !== 'undefined' ? window : null, function (browserRoot) {
  'use strict';

  var PRODUCTION_BASE_URL =
    'https://lucky-name.momcozy.com/momcozy/webbackend/api/v1/';
  var BETA_BASE_URL =
    'https://lucky-name.momcozy.com/beta/momcozy/webbackend/api/v1/';
  /**
   * Baby Name API 环境开关。
   * - 'auto': 线上模板走生产接口，副本模板（upgrade/dev/copy/preview）走 beta；
   *          无模板信息时再按 hostname（momcozy.com → 线上，其余 → beta）
   * - 'beta': 强制 beta
   * - 'production': 强制线上
   *
   * 覆盖优先级：?baby_name_api= → localStorage yg-baby-name-api-env →
   * window.__MC_BABY_NAME_API_ENV__ → flow config → 本常量。
   */
  var API_ENV = 'auto';
  var COPY_TEMPLATE_HINTS = [
    'upgrade',
    'dev',
    'copy',
    'preview',
    'test',
    'beta'
  ];
  var API_ENV_STORAGE_KEY = 'yg-baby-name-api-env';
  var API_ENV_QUERY_KEY = 'baby_name_api';
  var DEFAULT_TIMEOUT = 30000;
  var APIFOX_PROJECT_ID = 6087876;
  var AUTH_STORAGE_KEY = 'yg-product-momcozy-token';
  var DEFAULT_COOKIE_NAME = 'momcozy-token';
  var TRUSTED_API_HOSTS = ['lucky-name.momcozy.com'];
  var ENDPOINTS = {
    generator: 'baby_names_generator',
    info: 'baby_names_info_page',
    history: 'baby_names_historical_curve',
    savedNames: 'my_baby_name_list'
  };

  function BabyNameRequestError(message, details) {
    details = details || {};
    Error.call(this, message);
    this.name = 'BabyNameRequestError';
    this.message = message;
    this.code = details.code || 'NETWORK_ERROR';
    this.status = details.status || 0;
    this.statusText = details.statusText || '';
    this.data = details.data === undefined ? null : details.data;
    this.url = details.url || '';
    this.method = details.method || 'GET';
    this.cause = details.cause;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BabyNameRequestError);
    }
  }

  BabyNameRequestError.prototype = Object.create(Error.prototype);
  BabyNameRequestError.prototype.constructor = BabyNameRequestError;

  function isProductionHostname(hostname) {
    return hostname === 'momcozy.com' || hostname.endsWith('.momcozy.com');
  }

  function isCopyTemplateSuffix(suffix) {
    var value = String(suffix || '').toLowerCase();
    if (!value) return false;
    for (var index = 0; index < COPY_TEMPLATE_HINTS.length; index += 1) {
      if (value.indexOf(COPY_TEMPLATE_HINTS[index]) !== -1) return true;
    }
    return false;
  }

  function getFlowConfig() {
    try {
      var doc = browserRoot && browserRoot.document;
      var configEl = doc
        ? doc.querySelector('[data-baby-name-flow-config]')
        : null;
      if (!configEl) return null;
      return JSON.parse(configEl.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function resolveAutoApiEnv(config) {
    if (config && isCopyTemplateSuffix(config.templateSuffix)) return 'beta';
    if (config && config.templateSuffix) return 'production';
    var hostname =
      browserRoot && browserRoot.location ? browserRoot.location.hostname : '';
    return isProductionHostname(hostname) ? 'production' : 'beta';
  }

  function normalizeApiEnv(value) {
    var env = String(value || '')
      .trim()
      .toLowerCase();
    if (env === 'prod' || env === 'live' || env === 'online')
      return 'production';
    if (env === 'dev' || env === 'test' || env === 'staging') return 'beta';
    if (env === 'production' || env === 'beta' || env === 'auto') return env;
    return '';
  }

  function getQueryApiEnv() {
    try {
      if (!browserRoot || !browserRoot.location) return '';
      var params = new URLSearchParams(browserRoot.location.search || '');
      return normalizeApiEnv(params.get(API_ENV_QUERY_KEY));
    } catch (error) {
      return '';
    }
  }

  function getStoredApiEnv() {
    try {
      var storage = browserRoot && browserRoot.localStorage;
      return storage
        ? normalizeApiEnv(storage.getItem(API_ENV_STORAGE_KEY))
        : '';
    } catch (error) {
      return '';
    }
  }

  function getWindowApiEnv() {
    var fromWindow = normalizeApiEnv(
      browserRoot && browserRoot.__MC_BABY_NAME_API_ENV__
    );
    if (fromWindow && fromWindow !== 'auto') return fromWindow;

    var config = getFlowConfig();
    if (!config) return fromWindow;

    var env = normalizeApiEnv(config.apiEnv);
    if (env && env !== 'auto') return env;
    return resolveAutoApiEnv(config);
  }

  function resolveApiEnv() {
    return (
      getQueryApiEnv() ||
      getStoredApiEnv() ||
      getWindowApiEnv() ||
      normalizeApiEnv(API_ENV) ||
      'auto'
    );
  }

  function resolveBaseUrl() {
    var env = resolveApiEnv();
    if (env === 'beta') return BETA_BASE_URL;
    if (env === 'production') return PRODUCTION_BASE_URL;
    return resolveAutoApiEnv(getFlowConfig()) === 'production'
      ? PRODUCTION_BASE_URL
      : BETA_BASE_URL;
  }

  function normalizeBaseUrl(baseUrl) {
    return baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
  }

  function isAbsoluteUrl(url) {
    return /^https?:\/\//i.test(url);
  }

  function getUrlOrigin(url) {
    try {
      if (typeof URL === 'function') return new URL(url).origin;
      var match = String(url || '').match(/^(https?:\/\/[^/?#]+)/i);
      return match ? match[1] : '';
    } catch (error) {
      return '';
    }
  }

  function getHostname(url) {
    try {
      if (typeof URL === 'function') return new URL(url).hostname.toLowerCase();
      var match = String(url || '').match(/^https?:\/\/([^/?#:]+)/i);
      return match ? match[1].toLowerCase() : '';
    } catch (error) {
      return '';
    }
  }

  function isTrustedApiUrl(url, trustedOrigins) {
    if (!url || !/^https:\/\//i.test(url)) return false;
    var hostname = getHostname(url);
    if (TRUSTED_API_HOSTS.indexOf(hostname) !== -1) return true;
    var origin = getUrlOrigin(url);
    if (!origin) return false;
    trustedOrigins = trustedOrigins || [];
    for (var index = 0; index < trustedOrigins.length; index += 1) {
      if (origin === trustedOrigins[index]) return true;
    }
    return false;
  }

  function appendParams(url, params) {
    if (!params) return url;

    var searchParams = new URLSearchParams();
    Object.keys(params).forEach(function (key) {
      var value = params[key];
      if (value === null || value === undefined) return;

      if (Array.isArray(value)) {
        value.forEach(function (item) {
          if (item !== null && item !== undefined)
            searchParams.append(key, item);
        });
        return;
      }

      searchParams.append(key, value);
    });

    var query = searchParams.toString();
    if (!query) return url;
    return url + (url.includes('?') ? '&' : '?') + query;
  }

  function buildUrl(baseUrl, url, params) {
    var finalUrl = isAbsoluteUrl(url)
      ? url
      : normalizeBaseUrl(baseUrl) + url.replace(/^\//, '');
    return appendParams(finalUrl, params);
  }

  function normalizeToken(value) {
    if (typeof value !== 'string') return null;
    var token = value.trim();
    return token || null;
  }

  function getCookie(name, doc) {
    doc = doc || (browserRoot && browserRoot.document);
    if (!name || !doc) return null;

    var cookies = doc.cookie ? doc.cookie.split(';') : [];
    for (var index = 0; index < cookies.length; index += 1) {
      var cookie = cookies[index].trim();
      if (cookie.indexOf(name + '=') === 0) {
        return normalizeToken(cookie.substring(name.length + 1));
      }
    }
    return null;
  }

  function getLocalStorageToken(root) {
    try {
      var storage = root && root.localStorage;
      return storage && typeof storage.getItem === 'function'
        ? normalizeToken(storage.getItem(AUTH_STORAGE_KEY))
        : null;
    } catch (error) {
      return null;
    }
  }

  function getAuthToken(root, doc) {
    root = root || browserRoot;
    doc = doc || (root && root.document);
    var hostname =
      root && root.location ? String(root.location.hostname || '') : '';
    var config = root && root.__MC__CONFIG__;
    var cookieName =
      config && config.cookieName ? config.cookieName : DEFAULT_COOKIE_NAME;

    if (!isProductionHostname(hostname)) {
      var localToken = getLocalStorageToken(root);
      if (localToken) return localToken;
    }

    return getCookie(cookieName, doc);
  }

  function hasAuthToken(root, doc) {
    return Boolean(getAuthToken(root, doc));
  }

  function getDefaultToken() {
    return getAuthToken();
  }

  function generateTime(timestamp, randomValue) {
    return (
      randomValue.toString(16) +
      (
        (((((timestamp >> 24) & 0xff) ^ randomValue) & 0x7f) << 24) +
        ((((timestamp >> 16) & 0xff) ^ randomValue) << 16) +
        ((((timestamp >> 8) & 0xff) ^ randomValue) << 8) +
        ((timestamp & 0xff) ^ randomValue)
      )
        .toString(16)
        .padStart(2, '0')
    );
  }

  function mergeStrings(first, second) {
    var result = '';
    var maxLength = Math.max(first.length, second.length);

    for (var index = 0; index < maxLength; index += 1) {
      if (index < first.length) result += first[index];
      if (index < second.length) result += second[index];
    }
    return result;
  }

  async function hashString(value) {
    var cryptoApi = browserRoot && browserRoot.crypto;
    if (cryptoApi && cryptoApi.subtle) {
      var encoder = new TextEncoder();
      var data = encoder.encode(value);
      var hashBuffer = await cryptoApi.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(hashBuffer))
        .map(function (item) {
          return item.toString(16).padStart(2, '0');
        })
        .join('');
    }

    var hash = 0;
    for (var index = 0; index < value.length; index += 1) {
      hash = (hash << 5) - hash + value.charCodeAt(index);
      hash |= 0;
    }
    return hash.toString(16);
  }

  async function generateBrowserFingerprint() {
    if (!browserRoot || !browserRoot.document || !browserRoot.navigator)
      return null;

    var components = [];
    var navigatorApi = browserRoot.navigator;
    var screenApi = browserRoot.screen || {};

    components.push(navigatorApi.userAgent || '');
    components.push(
      (screenApi.width || 0) +
        'x' +
        (screenApi.height || 0) +
        'x' +
        (screenApi.colorDepth || 0)
    );
    components.push(new Date().getTimezoneOffset());
    components.push(
      navigatorApi.language ||
        navigatorApi.userLanguage ||
        navigatorApi.browserLanguage ||
        ''
    );

    if (navigatorApi.plugins) {
      components.push(
        Array.from(navigatorApi.plugins)
          .map(function (plugin) {
            return plugin.name + plugin.filename + plugin.description;
          })
          .join(';')
      );
    }

    components.push(navigatorApi.platform || '');
    components.push(Boolean(navigatorApi.cookieEnabled));
    components.push(Boolean(browserRoot.localStorage));
    components.push(Boolean(browserRoot.sessionStorage));

    try {
      var canvas = browserRoot.document.createElement('canvas');
      var context = canvas.getContext('2d');
      canvas.width = 200;
      canvas.height = 50;
      context.font = '20px Arial';
      context.fillStyle = 'rgb(100, 200, 300)';
      context.fillText('浏览器指纹', 10, 30);
      context.fillStyle = 'rgb(200, 100, 50)';
      context.fillText('唯一标识', 10, 45);
      components.push(canvas.toDataURL());
    } catch (error) {
      components.push('canvas-unsupported');
    }

    try {
      var webglCanvas = browserRoot.document.createElement('canvas');
      var webgl =
        webglCanvas.getContext('webgl') ||
        webglCanvas.getContext('experimental-webgl');
      if (webgl) {
        components.push(
          webgl.getParameter(webgl.RENDERER) + webgl.getParameter(webgl.VENDOR)
        );
      }
    } catch (error) {
      components.push('webgl-unsupported');
    }

    var fontList = [
      'Arial',
      'Times New Roman',
      'Courier New',
      'Verdana',
      'Georgia'
    ];
    components.push(
      fontList
        .filter(function (font) {
          var testElement = browserRoot.document.createElement('span');
          testElement.style.fontFamily = font;
          return testElement.offsetWidth !== 0;
        })
        .join(',')
    );

    var fingerprintString = components.join('###');
    var domainHash = await hashString('momcozy.com');
    var fingerprintHash = await hashString(fingerprintString);
    var merged = mergeStrings(domainHash, fingerprintHash);
    return merged.substring(0, Math.floor(merged.length / 2));
  }

  function getGlobalValue(name) {
    if (browserRoot && browserRoot[name]) return browserRoot[name];
    if (typeof globalThis !== 'undefined' && globalThis[name])
      return globalThis[name];
    return null;
  }

  function isJsonBody(data) {
    if (Array.isArray(data)) return true;
    if (!data || Object.prototype.toString.call(data) !== '[object Object]')
      return false;
    var prototype = Object.getPrototypeOf(data);
    return prototype === Object.prototype || prototype === null;
  }

  async function parseResponse(response, url, method) {
    var text = await response.text();
    if (!text) return null;

    var contentType =
      response.headers && response.headers.get
        ? response.headers.get('content-type') || ''
        : '';
    var expectsJson = /(^|\/)json($|;)|\+json($|;)/i.test(contentType);
    if (!expectsJson) return text;

    try {
      return JSON.parse(text);
    } catch (cause) {
      throw new BabyNameRequestError(
        'Failed to parse the Baby Name API response',
        {
          code: 'PARSE_ERROR',
          status: response.status,
          statusText: response.statusText,
          data: text,
          url: url,
          method: method,
          cause: cause
        }
      );
    }
  }

  function createClient(config) {
    config = config || {};

    var explicitBaseUrl = config.baseUrl
      ? normalizeBaseUrl(config.baseUrl)
      : '';
    var defaultTimeout =
      config.timeout === undefined ? DEFAULT_TIMEOUT : config.timeout;
    var fetchImpl = config.fetchImpl || getGlobalValue('fetch');
    var HeadersConstructor = getGlobalValue('Headers');
    var AbortControllerConstructor = getGlobalValue('AbortController');
    var getToken = config.getToken || getDefaultToken;
    var getFingerprint = config.getFingerprint || generateBrowserFingerprint;
    var fingerprintPromise = null;

    function resolveFingerprint() {
      if (!fingerprintPromise) {
        fingerprintPromise = Promise.resolve()
          .then(getFingerprint)
          .catch(function () {
            return null;
          });
      }
      return fingerprintPromise;
    }

    async function request(options) {
      options = options || {};
      if (!options.url) throw new TypeError('Baby Name request requires a URL');
      if (!fetchImpl)
        throw new TypeError('Fetch is not available in this environment');
      if (!HeadersConstructor)
        throw new TypeError('Headers is not available in this environment');

      var method = (options.method || 'GET').toUpperCase();
      var baseUrl = explicitBaseUrl || normalizeBaseUrl(resolveBaseUrl());
      var trustedOrigins = [getUrlOrigin(baseUrl)].filter(Boolean);
      var url = buildUrl(baseUrl, options.url, options.params);

      // Reject untrusted absolute URLs before attaching credentials or browser fingerprint.
      if (!isTrustedApiUrl(url, trustedOrigins)) {
        throw new BabyNameRequestError('Baby Name API URL is not allowed', {
          code: 'UNTRUSTED_URL',
          status: 0,
          statusText: '',
          data: null,
          url: url,
          method: method
        });
      }

      var headers = new HeadersConstructor();
      headers.set('Cache-Control', 'no-cache');
      headers.set(
        'X-Scope',
        generateTime(String(Math.floor(Date.now() / 1000)), 100)
      );

      var fingerprint = await resolveFingerprint();
      if (fingerprint) headers.set('X-Session', fingerprint);

      var token = await Promise.resolve().then(getToken);
      if (token) headers.set('Authorization', token);

      var requestHeaders = new HeadersConstructor(options.headers || {});
      requestHeaders.forEach(function (value, key) {
        headers.set(key, value);
      });

      var body = options.data;
      if (body !== null && body !== undefined && isJsonBody(body)) {
        body = JSON.stringify(body);
        if (!headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json;charset=UTF-8');
        }
      }

      var timeout =
        options.timeout === undefined ? defaultTimeout : options.timeout;
      var controller = AbortControllerConstructor
        ? new AbortControllerConstructor()
        : null;
      var signal = controller ? controller.signal : options.signal;
      var timeoutId = null;
      var timedOut = false;
      var onExternalAbort = null;

      if (controller && options.signal) {
        onExternalAbort = function () {
          controller.abort(options.signal.reason);
        };
        if (options.signal.aborted) {
          onExternalAbort();
        } else {
          options.signal.addEventListener('abort', onExternalAbort, {
            once: true
          });
        }
      }

      if (controller && timeout > 0) {
        timeoutId = setTimeout(function () {
          timedOut = true;
          controller.abort();
        }, timeout);
      }

      try {
        var response = await fetchImpl(url, {
          method: method,
          headers: headers,
          body: method === 'GET' || method === 'HEAD' ? undefined : body,
          signal: signal
        });
        var data = await parseResponse(response, url, method);

        if (!response.ok) {
          throw new BabyNameRequestError('Baby Name API request failed', {
            code: 'HTTP_ERROR',
            status: response.status,
            statusText: response.statusText,
            data: data,
            url: url,
            method: method
          });
        }

        return data;
      } catch (error) {
        if (error instanceof BabyNameRequestError) throw error;

        throw new BabyNameRequestError(
          timedOut
            ? 'Baby Name API request timed out'
            : 'Baby Name API network request failed',
          {
            code: timedOut ? 'TIMEOUT' : 'NETWORK_ERROR',
            status: 0,
            statusText: '',
            data: null,
            url: url,
            method: method,
            cause: error
          }
        );
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        if (options.signal && onExternalAbort) {
          options.signal.removeEventListener('abort', onExternalAbort);
        }
      }
    }

    /**
     * 生成 Baby Name 候选名称。
     * Apifox: POST /baby_names_generator
     *
     * @param {Object} data - 名称筛选条件及分页参数。
     * @param {Object} [options] - 通用请求配置，例如 headers、timeout 或 signal。
     * @returns {Promise<*>} API 响应数据。
     */
    function generateNames(data, options) {
      return request(
        Object.assign({}, options, {
          url: ENDPOINTS.generator,
          method: 'POST',
          data: data
        })
      );
    }

    /**
     * 获取指定 Baby Name 的详情页数据。
     * Apifox: GET /baby_names_info_page?name={name}
     */
    function getNameInfo(name, options) {
      return request(
        Object.assign({}, options, {
          url: ENDPOINTS.info,
          method: 'GET',
          params: Object.assign({}, options && options.params, { name: name })
        })
      );
    }

    /**
     * 获取指定 Baby Name 的历史流行趋势曲线。
     * Apifox: GET /baby_names_historical_curve?name={name}
     */
    function getNameHistory(name, options) {
      return request(
        Object.assign({}, options, {
          url: ENDPOINTS.history,
          method: 'GET',
          params: Object.assign({}, options && options.params, { name: name })
        })
      );
    }

    /**
     * 获取当前顾客收藏的全部 Baby Name。
     * Apifox: GET /my_baby_name_list
     */
    function getMyBabyNames(options) {
      return request(
        Object.assign({}, options, {
          url: ENDPOINTS.savedNames,
          method: 'GET'
        })
      );
    }

    /**
     * 为当前顾客收藏一个 Baby Name。
     * Apifox: POST /my_baby_name_list
     *
     * @param {{baby_name: Object, last_name?: string}} data - 收藏名称的请求数据。
     */
    function addMyBabyName(data, options) {
      return request(
        Object.assign({}, options, {
          url: ENDPOINTS.savedNames,
          method: 'POST',
          data: data
        })
      );
    }

    /**
     * 更新已收藏的 Baby Name。
     * Apifox: PUT /my_baby_name_list?baby_name_id={babyNameId}
     */
    function updateMyBabyName(babyNameId, data, options) {
      return request(
        Object.assign({}, options, {
          url: ENDPOINTS.savedNames,
          method: 'PUT',
          params: Object.assign({}, options && options.params, {
            baby_name_id: babyNameId
          }),
          data: data
        })
      );
    }

    /**
     * 删除已收藏的 Baby Name。
     * Apifox: DELETE /my_baby_name_list?baby_name_id={babyNameId}
     */
    function deleteMyBabyName(babyNameId, options) {
      return request(
        Object.assign({}, options, {
          url: ENDPOINTS.savedNames,
          method: 'DELETE',
          params: Object.assign({}, options && options.params, {
            baby_name_id: babyNameId
          })
        })
      );
    }

    /**
     * 调整已收藏 Baby Name 的顺序，目标位置从 1 开始计数。
     * Apifox: PATCH /my_baby_name_list
     *
     * @param {{baby_name: {id: string, move: number}}} data - 排序请求数据。
     */
    function sortMyBabyName(data, options) {
      return request(
        Object.assign({}, options, {
          url: ENDPOINTS.savedNames,
          method: 'PATCH',
          data: data
        })
      );
    }

    return {
      request: request,
      get: function (url, params, options) {
        return request(
          Object.assign({}, options, {
            url: url,
            method: 'GET',
            params: params
          })
        );
      },
      post: function (url, data, options) {
        return request(
          Object.assign({}, options, { url: url, method: 'POST', data: data })
        );
      },
      put: function (url, data, options) {
        return request(
          Object.assign({}, options, { url: url, method: 'PUT', data: data })
        );
      },
      patch: function (url, data, options) {
        return request(
          Object.assign({}, options, { url: url, method: 'PATCH', data: data })
        );
      },
      delete: function (url, data, options) {
        return request(
          Object.assign({}, options, { url: url, method: 'DELETE', data: data })
        );
      },
      generateNames: generateNames,
      getNameInfo: getNameInfo,
      getNameHistory: getNameHistory,
      getMyBabyNames: getMyBabyNames,
      addMyBabyName: addMyBabyName,
      updateMyBabyName: updateMyBabyName,
      deleteMyBabyName: deleteMyBabyName,
      sortMyBabyName: sortMyBabyName
    };
  }

  var defaultClient = createClient();

  return {
    APIFOX_PROJECT_ID: APIFOX_PROJECT_ID,
    AUTH_STORAGE_KEY: AUTH_STORAGE_KEY,
    TRUSTED_API_HOSTS: TRUSTED_API_HOSTS,
    API_ENV: API_ENV,
    API_ENV_STORAGE_KEY: API_ENV_STORAGE_KEY,
    API_ENV_QUERY_KEY: API_ENV_QUERY_KEY,
    PRODUCTION_BASE_URL: PRODUCTION_BASE_URL,
    BETA_BASE_URL: BETA_BASE_URL,
    resolveApiEnv: resolveApiEnv,
    resolveBaseUrl: resolveBaseUrl,
    BabyNameRequestError: BabyNameRequestError,
    isTrustedApiUrl: isTrustedApiUrl,
    getAuthToken: getAuthToken,
    hasAuthToken: hasAuthToken,
    createClient: createClient,
    request: defaultClient.request,
    get: defaultClient.get,
    post: defaultClient.post,
    put: defaultClient.put,
    patch: defaultClient.patch,
    delete: defaultClient.delete,
    generateNames: defaultClient.generateNames,
    getNameInfo: defaultClient.getNameInfo,
    getNameHistory: defaultClient.getNameHistory,
    getMyBabyNames: defaultClient.getMyBabyNames,
    addMyBabyName: defaultClient.addMyBabyName,
    updateMyBabyName: defaultClient.updateMyBabyName,
    deleteMyBabyName: defaultClient.deleteMyBabyName,
    sortMyBabyName: defaultClient.sortMyBabyName
  };
});
